import { useAuth } from "@clerk/clerk-react";
import { useOverlayScrollbars } from "overlayscrollbars-react";
import { ReactNode, useEffect, useRef, useState } from "react";

import { Navigation, Values } from "@/constants";
import { useDevice } from "@/hooks/useDevice";
import { useHasMounted } from "@/hooks/useHasMounted";
import { ButtonType, DropdownType, MaxWidthType } from "@/layouts/@types";
import { PublicFooterLayout } from "@/layouts/PublicFooterLayout";
import { PublicTopbar } from "@/layouts/PublicLayout/PublicTopbar";
import { cn } from "@/components/ui/utils";

function Inner({
  children,
  getFooter,
  getTopbar,
  isFooterBorderHidden = false,
  isHorizontalPaddingIncluded = null,
  isPublicLayout = false,
  isVerticalPaddingIncluded = null,
  backgroundColor = "var(--muted)",
  className,
}: {
  children: ReactNode;
  getFooter?: (maxWidth?: "xs" | "sm" | "md" | "lg" | "xl") => ReactNode;
  getTopbar?: () => ReactNode;
  isFooterBorderHidden?: boolean;
  isHorizontalPaddingIncluded?: boolean;
  isPublicLayout?: boolean;
  isVerticalPaddingIncluded?: boolean;
  /** The page's ground, one of the theme's backgrounds: the window's too. */
  backgroundColor?: string;
  /** Classes for what the page holds, between its topbar and its footer. */
  className?: string;
}) {
  const footer = getFooter ? getFooter() : null;
  const topbar = getTopbar ? getTopbar() : null;

  const scrollRef = useRef(null);
  const [initialize] = useOverlayScrollbars();

  useEffect(() => {
    if (document) {
      initialize(document.body);
    }
  }, [initialize]);

  const topbarHeight = isPublicLayout
    ? Values.PUBLIC_TOPBAR_HEIGHT
    : Values.TOPBAR_HEIGHT;

  const isDesktop = true;

  useEffect(() => {
    document.body.style.backgroundColor = backgroundColor;

    // Only apply these settings if they aren't already set with higher priority
    if (
      document.body.style.getPropertyPriority("overscrollBehaviorX") !==
      "important"
    ) {
      document.body.style.setProperty(
        "overscrollBehaviorX",
        "none",
        "important",
      );
    }
    if (
      document.body.style.getPropertyPriority("overscrollBehaviorY") !==
      "important"
    ) {
      document.body.style.setProperty(
        "overscrollBehaviorY",
        "none",
        "important",
      );
    }
  }, [backgroundColor]);

  return (
    <>
      {isDesktop && (
        <div
          ref={scrollRef}
          id="back-to-top-anchor"
          className="min-w-full max-w-[100vw] min-h-screen flex-col items-center relative overflow-x-clip overscroll-x-contain overscroll-y-contain"
          style={{ backgroundColor }}
        >
          {topbar && (
            <div
              className={cn(
                "w-full [backdrop-filter:blur(16px)] [-webkit-backdrop-filter:blur(16px)] bg-[color-mix(in_srgb,var(--background)_75%,transparent)] sticky top-0 overscroll-x-contain bottom-0",
                isPublicLayout ? "h-16" : "h-12",
                "[border-bottom-width:1px] [border-bottom-style:solid]",
                "border-border [transition:all_0.3s] z-1000",
              )}
            >
              {topbar}
            </div>
          )}

          {/* Body */}
          <div
            className={cn(
              "flex flex-col min-w-full max-w-full relative",
              isHorizontalPaddingIncluded === true
                ? "pl-12 pr-12"
                : "pl-0 pr-0",
              isVerticalPaddingIncluded ? "pt-8 pb-8" : "pt-0 pb-0",
              className,
              "z-998",
            )}
            style={{
              backgroundColor,
              minHeight: `calc(100vh - ${topbar ? topbarHeight : 0}px)`,
            }}
          >
            {children}
          </div>

          {footer != null && (
            <div
              className={cn(
                "mt-0",
                isFooterBorderHidden
                  ? "[border-top-width:0]"
                  : "[border-top-width:1px]",
                "[border-top-style:solid] border-border min-w-full flex justify-center items-center p-4 md:p-8",
              )}
            >
              <div
                className="flex justify-center items-center h-full w-full rounded-lg max-w-350"
                style={{
                  backgroundColor: "oklch(0.269 0 0)",
                }}
              >
                <div className="max-w-350 w-full grow pl-4 pr-4 md:pl-8 md:pr-8 lg:pl-12 lg:pr-12">
                  {footer}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function LayoutRouter({
  children,
  getTopbar = null,
  isFooterBorderHidden = false,
  isFooterVisible = null,
  isHorizontalPaddingIncluded = null,
  isPublicLayout = false,
  isRulersIncluded = null,
  isVerticalPaddingIncluded = null,
  backgroundColor,
  className,
  topbarMaxWidth = null,
}: {
  children?: ReactNode;
  getTopbar?: (() => ReactNode) | null;
  isFooterBorderHidden?: boolean;
  isHorizontalPaddingIncluded?: boolean;
  isPublicLayout?: boolean;
  isRulersIncluded?: boolean;
  isVerticalPaddingIncluded?: boolean;
  isFooterVisible?: boolean;
  /** The page's ground, one of the theme's backgrounds: ``background.level1`` unless told. */
  backgroundColor?: string;
  /** Classes for what the page holds, between its topbar and its footer. */
  className?: string;
  topbarMaxWidth?: MaxWidthType;
}) {
  const { isSignedIn } = useAuth();
  const [, setIsOpen] = useState(false);
  const hasMounted = useHasMounted();
  const { isNarrow } = useDevice();

  if (isRulersIncluded === null) {
    isRulersIncluded = isPublicLayout;
  }

  useEffect(() => {
    if (!isNarrow) {
      setIsOpen(false);
    }
  }, [isNarrow]);

  // Main topbar buttons
  const navigationButtons: {
    component: ButtonType;
    dropdown: DropdownType[];
  }[] = [];

  // Main call-2-action buttons
  const actionButtons: ButtonType[] = isSignedIn
    ? []
    : [
        { name: "Sign in", path: Navigation.ROUTES.AUTH.LOGIN },
        {
          name: "Join waitlist",
          path: Navigation.ROUTES.AUTH.WAITLIST,
        },
      ];

  if (isSignedIn && isPublicLayout) {
    actionButtons.unshift({
      name: "Dashboard",
      path: Navigation.ROUTES.APP.PAGE,
    });
  }

  // Uncomment when we have public pricing available
  if (isPublicLayout) {
    navigationButtons.push(
      {
        component: {
          name: "Platform",
          path: "/platform",
        },
        dropdown: Navigation.PLATFORM_ITEMS,
      },
      {
        component: {
          name: "Solutions",
          path: "/solutions",
        },
        dropdown: Navigation.SOLUTION_ITEMS,
      },
      {
        component: {
          name: "Pricing",
          path: "/pricing",
        },
        dropdown: [],
      },
    );
  }

  if (!hasMounted) {
    return null;
  }

  return (
    (isPublicLayout || isSignedIn) && (
      <>
        <Inner
          isVerticalPaddingIncluded={isVerticalPaddingIncluded}
          isHorizontalPaddingIncluded={isHorizontalPaddingIncluded}
          isFooterBorderHidden={isFooterBorderHidden}
          isPublicLayout={isPublicLayout}
          getTopbar={() =>
            getTopbar ? (
              getTopbar()
            ) : (
              <PublicTopbar
                navigationButtons={navigationButtons}
                actionButtons={actionButtons}
                maxWidth={topbarMaxWidth}
              />
            )
          }
          getFooter={() =>
            (isPublicLayout && isFooterVisible !== false) ||
            isFooterVisible === true ? (
              <PublicFooterLayout />
            ) : null
          }
          backgroundColor={backgroundColor}
          className={className}
        >
          {children}
        </Inner>
      </>
    )
  );
}
